import { Fragment, useMemo, type ReactNode } from 'react';
import { EarningStrip } from '../../components/SchemeEarning';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Wallet, CreditCard, Plus, FileText, Undo2, MessageSquareWarning,
  Package, Truck, PackageCheck, ClipboardCheck, ClipboardList, Phone, ChevronRight,
  ReceiptText, TrendingUp, MapPin, MessageCircle, Landmark, CalendarClock, Sprout,
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from 'recharts';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { orderStatusLabel, retailerStatusLabel } from '../../lib/statusTone';
import { useAuthStore } from '../../store/auth';
import { useBadges } from '../../hooks/useBadges';
import { Card, Skeleton, Alert, Button, StatCard, EntityAvatar, cn } from '../../ui';
import { Pill } from '../../components/TwDataTable';
import { CopyValue } from '../../components/CopyValue';
import { RoleBadge } from '../../components/RoleBadge';
import { formatIst, formatIstDate } from '../../lib/time';
import { inr, inr2, inrShort } from '../../lib/format';
import { PALETTE } from '../../lib/palette';

// ── Money formatting — mirrors MyAccountPage / OrdersListPage EXACTLY (money-formatting-precision):
//    `inr` = rounded whole rupees for KPI tiles / axes; `inr2` = paise-exact for payable amounts.
//    Both come from lib/format so this page cannot drift from the rest of the app.


// Order status → pill tone + label — COPIED from OrdersListPage so a status reads identically here.
const STATUS_PILL: Record<string, string> = {
  DRAFT: 'default', PLACED: 'blue', AWAITING_PRODUCTION: 'purple', IN_PRODUCTION: 'purple',
  AWAITING_TRANSFER: 'blue', APPROVED: 'blue', ACCEPTED: 'purple', PACKED: 'gold', DISPATCHED: 'orange',
  DELIVERED: 'green', INVOICED: 'purple', CANCELLED: 'red', CLOSED: 'default',
  ON_HOLD: 'orange', CORRECTION_REQUIRED: 'gold', REJECTED: 'red', BACKORDER: 'orange',
};
const statusLabel = (s: string) => (s ?? '').replace(/_/g, ' ');

// Invoice status → pill colour — matches InvoicesListPage's canonical tones exactly (OPEN=blue,
// PARTIAL=gold, PAID=green, OVERDUE=red, CANCELLED=neutral→default), so an invoice reads identically here.
const INV_PILL: Record<string, string> = {
  OPEN: 'blue', PARTIAL: 'gold', PAID: 'green', OVERDUE: 'red', CANCELLED: 'default',
};

/**
 * Retailer-meaningful order STAGES, in the order an order travels them.
 *
 * Every raw status maps to exactly one stage (a documented rollup); the recent-orders list below
 * still shows each order's exact status, so nothing is hidden or relabelled. Each stage carries its
 * own hue so the journey reads left-to-right as a coloured track rather than five identical rows —
 * these are a CATEGORICAL vocabulary (position in the journey), not a severity ramp.
 */
const STAGES: {
  key: string; label: string; icon: ReactNode; statuses: string[];
  chip: string; rail: string; tint: string;
}[] = [
  {
    key: 'awaiting', label: 'Awaiting approval', icon: <ClipboardCheck className="size-4" />,
    statuses: ['DRAFT', 'PLACED', 'ON_HOLD', 'CORRECTION_REQUIRED'],
    chip: 'bg-harvest-400/20 text-harvest-700 dark:text-harvest-300',
    rail: 'bg-harvest-400', tint: 'border-harvest-400/40 from-harvest-400/[0.14]',
  },
  {
    key: 'processing', label: 'Processing', icon: <ClipboardList className="size-4" />,
    statuses: ['APPROVED', 'ACCEPTED', 'AWAITING_TRANSFER', 'BACKORDER', 'AWAITING_PRODUCTION', 'IN_PRODUCTION'],
    chip: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
    rail: 'bg-sky-500', tint: 'border-sky-500/35 from-sky-500/[0.12]',
  },
  {
    key: 'packed', label: 'Packed', icon: <Package className="size-4" />,
    statuses: ['PACKED'],
    chip: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
    rail: 'bg-violet-500', tint: 'border-violet-500/35 from-violet-500/[0.12]',
  },
  {
    key: 'transit', label: 'In transit', icon: <Truck className="size-4" />,
    statuses: ['DISPATCHED'],
    chip: 'bg-warning-500/18 text-warning-600 dark:text-harvest-300',
    rail: 'bg-warning-500', tint: 'border-warning-500/40 from-warning-500/[0.14]',
  },
  {
    key: 'delivered', label: 'Delivered', icon: <PackageCheck className="size-4" />,
    statuses: ['DELIVERED', 'INVOICED', 'CLOSED'],
    chip: 'bg-brand-500/15 text-brand-700 dark:text-brand-300',
    rail: 'bg-brand-500', tint: 'border-brand-500/35 from-brand-500/[0.12]',
  },
];
// Stages an order is still MOVING through — everything before it lands. Used for the "in flight"
// sentence above the track, so "delivered" money is never counted as something still owed to you.
const IN_FLIGHT = new Set(['awaiting', 'processing', 'packed', 'transit']);

// The ageing buckets, in the order OutstandingAgingPage names them, each with the hue its lateness
// deserves — this ramp IS a severity scale, unlike the stage colours above.
const AGE_BUCKETS: { key: 'b0_30' | 'b31_60' | 'b61_90' | 'b_gt90'; label: string; bar: string; text: string }[] = [
  { key: 'b0_30', label: '0–30 days', bar: 'bg-brand-500', text: 'text-brand-700 dark:text-brand-300' },
  { key: 'b31_60', label: '31–60 days', bar: 'bg-harvest-400', text: 'text-harvest-700 dark:text-harvest-300' },
  { key: 'b61_90', label: '61–90 days', bar: 'bg-warning-500', text: 'text-warning-600 dark:text-harvest-300' },
  { key: 'b_gt90', label: 'Over 90 days', bar: 'bg-danger-500', text: 'text-danger-600 dark:text-danger-500' },
];

const pad = (n: number) => String(n).padStart(2, '0');
const monthKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const MONTH_LABEL = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Retailer home. Everything is self-scoped server-side to the logged-in retailer (ScopeService →
 * retailerId). It surfaces the dealer's account position (dues / credit — authoritative from
 * /retailers/me), their order pipeline & recent orders, recent invoices, and their sales rep — all
 * read-only; deep actions live on /orders, /billing/invoices and /account (full statement).
 *
 * DATA-CORRECTNESS RULES (do not "optimise" away):
 *  • Dues/credit come ONLY from /retailers/me.outstandingBalance (a single running balance: +owed,
 *    −advance). Never re-sum order balances — that double-counts advances/credit notes.
 *  • Headline active-order counts come from server-computed badges; the stage strip is a labelled
 *    rollup of the SIX-MONTH window this page fetches, and every panel drawn from that window says so.
 *  • Chart = monthly ORDER VALUE (Σ grandTotal), single series, one ₹ axis — never a dual axis.
 */
export function RetailerDashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const badges = useBadges();

  // 6-month window (whole months) for the order-value trend.
  const range = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    return { from: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`, to: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}` };
  }, []);
  // Every panel fed by the windowed fetch links to the SAME window on /orders, so "View all" opens
  // the set the panel counted rather than a different one (summary-panel-mirrors-its-page).
  const ordersHref = `/orders?from=${range.from}&to=${range.to}&includeClosed=true`;

  const { data: me, isLoading: meLoading, isError: meError } = useQuery({ queryKey: ['rd-me'], queryFn: () => request<any>({ url: '/retailers/me' }), retry: false });
  const { data: aging } = useQuery({ queryKey: ['rd-aging'], queryFn: () => request<any[]>({ url: '/outstanding/aging' }) });
  // One 6-month pull (incl. closed) powers the trend, recent-orders list AND stage counts.
  const { data: ordersRes, isLoading: ordLoading } = useQuery({
    queryKey: ['rd-orders', range.from, range.to],
    queryFn: () => requestAllPages('/orders', { from: range.from, to: range.to, includeClosed: true, sort: '-orderDate' }),
  });
  const { data: invoices, isLoading: invLoading } = useQuery({ queryKey: ['rd-invoices'], queryFn: () => request<any[]>({ url: '/invoices', params: { sort: '-invoiceDate' } }) });
  const { data: rep } = useQuery({ queryKey: ['rd-rep'], queryFn: () => request<any>({ url: '/profile/my-rep' }) });

  const orders = ordersRes?.data ?? [];

  // ── Account position — ONE authoritative passbook balance from /retailers/me (passbook spec §10/§11) ──
  // outstanding_balance is the single running ledger balance. DB positive = owes (Dr), negative = reusable
  // credit (Cr); userBalance B = −out. Every placed order already posted its obligation, so the raw balance is
  // the truth — no free-credit / committed-prepay split.
  const limit = Number(me?.creditLimit) || 0;
  const out = Number(me?.outstandingBalance) || 0;
  const owed = Math.max(0, out);        // outstanding due = max(0, −B)
  const advance = Math.max(0, -out);    // reusable account credit = max(0, B)
  const available = limit - out;        // availableToOrder = creditLimit + B
  const owes = out > 0.01, inCredit = advance > 0.01;
  // Share of the sanctioned limit already consumed. Capped for the BAR only (a dealer over their limit
  // still gets a full track, never a bar that overflows its box); the figures beside it stay exact.
  const usedPct = limit > 0 ? (owed / limit) * 100 : 0;
  const barPct = Math.max(0, Math.min(100, usedPct));

  // Ageing of dues — the retailer's own aging row(s), summed defensively. Bucket labels match
  // OutstandingAgingPage exactly (0-30 / 31-60 / 61-90 / >90 days).
  const ageing = useMemo(() => {
    const rows = aging ?? [];
    const sum = (k: string) => rows.reduce((a: number, r: any) => a + (Number(r?.[k]) || 0), 0);
    return { b0_30: sum('b0_30'), b31_60: sum('b31_60'), b61_90: sum('b61_90'), b_gt90: sum('b_gt90') };
  }, [aging]);
  const ageTotal = ageing.b0_30 + ageing.b31_60 + ageing.b61_90 + ageing.b_gt90;
  const pastDue = ageing.b31_60 + ageing.b61_90 + ageing.b_gt90; // beyond the current 0–30 bucket

  // ── Order trend — Σ grandTotal per month across the 6-month window ──
  const trend = useMemo(() => {
    const now = new Date();
    const buckets: { key: string; label: string; value: number; count: number }[] = [];
    const idx: Record<string, number> = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      idx[monthKey(d)] = buckets.length;
      buckets.push({ key: monthKey(d), label: `${MONTH_LABEL[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`, value: 0, count: 0 });
    }
    for (const o of orders) {
      if (o.status === 'CANCELLED' || o.status === 'REJECTED') continue; // cancelled ≠ purchases
      // Bucket by the LOCAL/IST month (same `monthKey` basis as the keys above), NOT the UTC slice — a
      // 1st-of-month early-IST order would otherwise fall into the previous month (or drop out of range).
      const k = o.orderDate ? monthKey(new Date(o.orderDate)) : '';
      if (k in idx) { buckets[idx[k]].value += Number(o.grandTotal) || 0; buckets[idx[k]].count += 1; }
    }
    return buckets;
  }, [orders]);
  const trendTotal = useMemo(() => trend.reduce((a, b) => a + b.value, 0), [trend]);
  const trendCount = useMemo(() => trend.reduce((a, b) => a + b.count, 0), [trend]);
  const bestMonth = useMemo(() => trend.reduce((best, b) => (b.value > (best?.value ?? 0) ? b : best), null as (typeof trend)[number] | null), [trend]);

  // ── Stage counts + the value still moving (labelled rollup of the same 6-month set) ──
  const { stageCounts, inFlightValue, inFlightCount } = useMemo(() => {
    const map: Record<string, number> = {};
    let value = 0, count = 0;
    for (const o of orders) {
      for (const st of STAGES) {
        if (!st.statuses.includes(o.status)) continue;
        map[st.key] = (map[st.key] ?? 0) + 1;
        if (IN_FLIGHT.has(st.key)) { value += Number(o.grandTotal) || 0; count += 1; }
        break;
      }
    }
    return { stageCounts: map, inFlightValue: value, inFlightCount: count };
  }, [orders]);

  const recentOrders = useMemo(() => orders.slice(0, 6), [orders]);
  const recentInvoices = useMemo(() => (invoices ?? []).filter((i: any) => i.status !== 'CANCELLED').slice(0, 6), [invoices]);

  // This dashboard is a DEALER self-view (/retailers/me). A non-retailer login (e.g. staff reaching it
  // by URL) has no retailer record → show the same friendly note MyAccountPage uses, not broken tiles.
  if (meError) return <Alert type="info" title="This dashboard is available for retailer accounts only." />;

  return (
    <div className="space-y-4">
      {/*
        * LETTERHEAD, not a banner. The page used to open on a slab of saturated brand green with white
        * text on it — the same objection already written down for the warehouse dashboard and the
        * company masthead: solid colour edge to edge is the shape of a promotional header, and it was
        * competing with the money figures immediately underneath. Quiet paper, one brand rule along
        * the top, a wash, and the content set as named registers instead of one block.
        */}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
        <div className="h-1 bg-linear-to-r from-brand-500 via-brand-600 to-brand-700" />
        <div className="bg-linear-to-br from-brand-50 via-brand-50/30 to-surface p-5 dark:from-brand-900/25 dark:via-surface dark:to-surface">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <div className="flex min-w-0 items-start gap-3.5">
              {/* The mark sits on a surface plate, not straight on the wash: EntityAvatar tints itself
                  to ~20% of its hue, which reads on plain paper but dissolves against the brand tint
                  behind it — the plate gives every hue the same ground to sit on. `square` because a
                  dealer is a BUSINESS; the circle is reserved for a person (the rep card below). */}
              <span className="mt-0.5 grid shrink-0 place-items-center rounded-card bg-surface p-1 shadow-e1">
                {/*
                  * TWO places a dealer's picture can live, and the hero has to try both.
                  *
                  * `retailers.photo_url` is the SHOP photo, which only staff can set from the dealer
                  * page. `users.profile_photo_url` is the one the dealer sets themselves under
                  * Profile → "Profile photo". The hero read only the first, so a dealer who uploaded
                  * their own picture still got flat initials here and had no way to change it.
                  * Dealer photo first (this masthead is the DEALER account), own photo as the
                  * fallback, initials only when neither exists.
                  */}
                <EntityAvatar name={me?.companyName ?? user?.fullName} src={me?.photoUrl || user?.profilePhotoUrl} size={48} square />
              </span>
              <div className="min-w-0">
                <div className="type-overline text-brand-600 dark:text-brand-400">Dealer account</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  <h1 className="type-hero text-content">Hi{user?.fullName ? `, ${user.fullName}` : ''}</h1>
                  {/* A dealer whose KYC is still pending needs to know it on the page they land on —
                      naming the gap is the point, so it is a pill beside the name rather than a line
                      of muted text further down. ACTIVE is the expected state and stays green+quiet. */}
                  {me?.status && (
                    <Pill color={me.status === 'ACTIVE' ? 'green' : me.status === 'SUSPENDED' || me.status === 'CLOSED' ? 'red' : 'gold'}>
                      {retailerStatusLabel(me.status)}
                    </Pill>
                  )}
                </div>
                {/*
                  * NO state sentence here. It read "₹12,480 payable · ₹2,37,520 available to order · …"
                  * — the first two figures are the two KPI tiles immediately below it, and the other
                  * two are already carried by the pipeline's own sentence and the Complaints badge.
                  * The prose-vs-figures argument does not survive the reader seeing the same number
                  * twice, 40px apart.
                  */}
              </div>
            </div>

            {/* The two things a dealer came here to do, as ONE action cluster. Stacked in a narrow
                column they read as a pile of unrelated blocks and left a band of empty paper beside
                them; a row is how a page header carries its actions, secondary then primary. */}
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Button variant="default" icon={<Wallet className="size-4" />} onClick={() => navigate('/account')} block className="sm:w-auto">
                Full statement
              </Button>
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => navigate('/orders/new')} block className="sm:w-auto">
                Place new order
              </Button>
            </div>
          </div>

          {/* The dealer's own particulars, below their own hairline and under a label — a bare strip of
              chips never said what it was a list OF. Each chip is a two-field pill: what it is at
              label weight, the value at full contrast, so the two read as different fields. */}
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-line pt-3.5">
            <span className="shrink-0 type-overline text-subtle">Your details</span>
            {meLoading ? <Skeleton className="h-6 w-64" /> : (
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {me?.code && <DetailChip label="Code"><CopyValue value={me.code} label="Dealer code">{me.code}</CopyValue></DetailChip>}
                {(me?.city || me?.pincode) && (
                  <DetailChip label={<MapPin className="size-3.5" />}>{[me.city, me.pincode].filter(Boolean).join(' · ')}</DetailChip>
                )}
                {me?.gstNumber && <DetailChip label="GSTIN"><CopyValue value={me.gstNumber} label="GSTIN">{me.gstNumber}</CopyValue></DetailChip>}
                {me?.creditPeriodDays != null && <DetailChip label={<CalendarClock className="size-3.5" />}>{me.creditPeriodDays}-day credit</DetailChip>}
                {me?.contactPerson && <DetailChip label="Contact">{me.contactPerson}</DetailChip>}
              </div>
            )}
            {/* The date, at the far end of the row a letterhead keeps its particulars in. It is what
                tells the reader when "last 6 months" and "still on the way" are counted from. */}
            <span className="type-label text-subtle sm:ml-auto">
              Today · <b className="type-label-strong text-content">{formatIst(new Date(), 'ddd, DD MMM YYYY')}</b>
            </span>
          </div>
        </div>
      </div>

      {/*
        * What this dealer is EARNING — under the letterhead, above everything else.
        *
        * The scheme's slabs live on "My Terms", a page a dealer has to choose to open. Where they
        * stand in those slabs, and how close the next step is, belongs where they land on login:
        * it is the only version of the information that can change what they order.
        */}
      <EarningStrip />

      {/*
        * MONEY BAND — the authoritative account position, on the app's one KPI component so a dealer's
        * tiles look and behave like every other band in the product (tint, rail, icon chip, drill).
        * Every tile that can be opened is opened by clicking it; a tile whose figure is zero is not
        * clickable, so there is no dead end (kpi-tiles-must-drill).
        */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={inCredit ? 'Account credit' : owes ? 'Outstanding' : 'Net balance'}
          value={meLoading ? <Skeleton className="h-7 w-24" /> : inr2(inCredit ? advance : owed)}
          hint={owes
            ? `Payable${limit > 0 ? ` · ${usedPct.toFixed(usedPct < 10 ? 1 : 0)}% of your limit used` : ''}`
            : inCredit ? 'Reusable credit — comes off your next order' : 'Nothing outstanding'}
          icon={<Wallet />}
          tone={owes ? 'danger' : inCredit ? 'success' : 'brand'}
          onClick={() => navigate('/account')}
        />
        <StatCard
          label="Available to order"
          value={meLoading ? <Skeleton className="h-7 w-24" /> : inr(available)}
          hint={limit > 0 ? `of ${inr(limit)} credit limit` : 'No credit limit set'}
          icon={<CreditCard />}
          tone={available > 0 ? 'brand' : limit > 0 ? 'danger' : 'neutral'}
          onClick={available > 0 ? () => navigate('/orders/new') : undefined}
        />
        <StatCard
          label="Ordered · last 6 months"
          value={ordLoading ? <Skeleton className="h-7 w-24" /> : inr(trendTotal)}
          hint={`${trendCount} order${trendCount === 1 ? '' : 's'}${bestMonth && bestMonth.value > 0 ? ` · best ${bestMonth.label}` : ''}`}
          icon={<TrendingUp />}
          tone="sky"
          onClick={trendCount > 0 ? () => navigate(ordersHref) : undefined}
        />
        <StatCard
          label="Open invoices"
          value={badges.openInvoices ?? 0}
          hint={(badges.openInvoices ?? 0) > 0 ? 'To pay or track' : 'Nothing to pay right now'}
          icon={<ReceiptText />}
          tone={(badges.openInvoices ?? 0) > 0 ? 'gold' : 'neutral'}
          onClick={(badges.openInvoices ?? 0) > 0 ? () => navigate('/billing/invoices') : undefined}
        />
      </div>

      {/* Trend + credit standing */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card
          className="xl:col-span-2"
          title={<span className="flex items-center gap-2"><ShoppingCart className="size-4 text-brand-600" /> Your order value</span>}
          extra={<span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-pill bg-brand-500" /> Monthly total · last 6 months</span>}
        >
          {ordLoading ? (
            <div className="py-6"><Skeleton className="h-56 w-full" /></div>
          ) : trendTotal > 0 ? (
            <>
              {/* The chart's own headline. An axis tells you the shape; this tells you the size —
                  and it is the sentence that says what the area under the curve adds up to. */}
              <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line pb-3">
                <span className="type-metric text-content">{inr(trendTotal)}</span>
                <span className="type-body text-muted">
                  across <b className="type-strong text-content">{trendCount}</b> order{trendCount === 1 ? '' : 's'}
                  {bestMonth && bestMonth.value > 0 && <> · biggest month <b className="type-strong text-content">{bestMonth.label}</b> at {inr(bestMonth.value)}</>}
                </span>
              </div>
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={trend} margin={{ top: 10, right: 14, left: 6, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rdOrdFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={PALETTE.brand[500]} stopOpacity={0.42} />
                      <stop offset="55%" stopColor={PALETTE.brand[500]} stopOpacity={0.16} />
                      <stop offset="98%" stopColor={PALETTE.brand[500]} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--gb-border)" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} minTickGap={8} />
                  <YAxis tickFormatter={inrShort} tickLine={false} axisLine={false} width={48} tick={{ fontSize: 11 }} />
                  <RTooltip content={<TrendTooltip />} cursor={{ stroke: 'var(--gb-border-strong)' }} />
                  {/* Dots are ON: a dealer with one month of orders in six is the common case, and a
                      lone data point on a line chart draws nothing at all without them. */}
                  <Area
                    type="monotone" dataKey="value" name="Order value"
                    stroke={PALETTE.brand[600]} strokeWidth={2.5} fill="url(#rdOrdFill)"
                    dot={{ r: 3, fill: PALETTE.brand[600], strokeWidth: 0 }}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--gb-surface)' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </>
          ) : (
            <EmptyPanel
              icon={<Sprout className="size-7" />}
              title="No orders in the last 6 months"
              detail="Your monthly purchase value will chart here as soon as you place an order."
              action={<Button variant="ghost" size="sm" icon={<Plus className="size-4" />} onClick={() => navigate('/orders/new')}>Place your first order</Button>}
            />
          )}
        </Card>

        {/*
          * CREDIT STANDING — the one panel that shows the dealer's dues as a PROPORTION rather than a
          * bare figure. The bar's denominator is named in the header and repeated in the legend, so
          * the fill is never a magnitude without a scale.
          */}
        <Card
          title={<span className="flex items-center gap-2"><Landmark className="size-4 text-brand-600" /> Credit standing</span>}
          extra={limit > 0 ? `of ${inr(limit)} limit` : undefined}
        >
          {meLoading ? (
            <div className="space-y-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-16 w-full" /></div>
          ) : limit > 0 ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className={cn('type-metric', owes ? 'text-danger-600' : inCredit ? 'text-brand-700 dark:text-brand-300' : 'text-content')}>
                    {usedPct > 0 ? `${usedPct.toFixed(usedPct < 10 ? 1 : 0)}%` : '0%'}
                  </span>
                  <span className="type-label text-muted">of your limit used</span>
                </div>
                {/* The track IS the credit limit; the fill is what is already drawn against it. */}
                <div className="mt-2 h-3 w-full overflow-hidden rounded-pill bg-surface-3 shadow-inset-1">
                  <div
                    className={cn('h-full rounded-pill transition-[width] duration-500',
                      usedPct >= 90 ? 'bg-linear-to-r from-danger-500 to-danger-600'
                        : usedPct >= 60 ? 'bg-linear-to-r from-harvest-400 to-warning-500'
                          : 'bg-linear-to-r from-brand-400 to-brand-600')}
                    style={{ width: `${barPct}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 type-label">
                  <span className="text-muted">Used <b className="type-strong text-content">{inr2(owed)}</b></span>
                  <span className="text-muted">Left <b className="type-strong text-content">{inr(Math.max(0, available))}</b></span>
                </div>
                {available < 0 && (
                  <div className="mt-2 rounded-control border border-danger-500/30 bg-danger-500/10 px-3 py-2 type-label text-danger-600">
                    Over your sanctioned limit by <b className="type-strong">{inr2(-available)}</b> — clear dues before the next order.
                  </div>
                )}
                {inCredit && (
                  <div className="mt-2 rounded-control border border-brand-500/30 bg-brand-500/10 px-3 py-2 type-label text-brand-700 dark:text-brand-300">
                    You are <b className="type-strong">{inr2(advance)}</b> in credit — it sits on top of the limit and comes off your next order.
                  </div>
                )}
              </div>

              {/* Ageing — drawn ONLY from the aging endpoint's own buckets, and only when they add up
                  to something. A dealer who owes money the aging report has not bucketed yet must not
                  be shown four empty bars implying the debt is current. */}
              {ageTotal > 0 ? (
                <div className="border-t border-line pt-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="type-overline text-subtle">How old the dues are</span>
                    {pastDue > 0 && <span className="type-label-strong text-warning-600">{inr(pastDue)} past due</span>}
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {AGE_BUCKETS.map((b) => {
                      const v = ageing[b.key];
                      if (v <= 0) return null;
                      const pct = (v / ageTotal) * 100;
                      return (
                        <div key={b.key} className="flex items-center gap-2">
                          <span className="w-[86px] shrink-0 type-label text-muted">{b.label}</span>
                          <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-pill bg-surface-3">
                            <span className={cn('block h-full rounded-pill', b.bar)} style={{ width: `${Math.max(3, pct)}%` }} />
                          </span>
                          <span className={cn('w-[74px] shrink-0 text-right type-label-strong tabular-nums', b.text)}>{inr(v)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : owed > 0 ? (
                <div className="border-t border-line pt-3 type-label text-subtle">
                  Your dues have not been bucketed by age yet — open the full statement for the entry-by-entry view.
                </div>
              ) : null}

              {/* A limit says how much may be drawn; the period says how long there is to clear it.
                  Half a dealer's terms were on the page without the other half. */}
              {me?.creditPeriodDays != null && (
                <div className="flex items-start gap-2 rounded-control border border-line bg-surface-2 px-3 py-2 type-label text-muted">
                  <CalendarClock className="mt-px size-4 shrink-0 text-brand-600 dark:text-brand-400" />
                  <span>
                    Your terms are <b className="type-strong text-content">{me.creditPeriodDays}-day credit</b> — an invoice falls
                    due {me.creditPeriodDays} days after it is billed.
                  </span>
                </div>
              )}

              <Button variant="ghost" size="sm" block icon={<ReceiptText className="size-4" />} onClick={() => navigate('/account')}>
                Open full statement
              </Button>
            </div>
          ) : (
            <EmptyPanel
              icon={<Landmark className="size-7" />}
              title="No credit limit set"
              detail="Your orders are on advance payment terms. Ask your sales rep about a credit limit."
            />
          )}
        </Card>
      </div>

      {/*
        * ORDER PIPELINE as a JOURNEY, left to right. The five stages used to be a vertical list of
        * rows that all looked identical, so nothing said they were consecutive — the one thing the
        * strip exists to communicate. Each stage now carries its own hue and its own count, greys out
        * when empty, and opens /orders already filtered to exactly the statuses it rolls up.
        */}
      <Card
        title={<span className="flex items-center gap-2"><Truck className="size-4 text-brand-600" /> Where your orders are</span>}
        extra="Last 6 months"
      >
        <div className="mb-3 type-body text-muted">
          {ordLoading ? 'Counting your orders…' : inFlightCount > 0 ? (
            <>
              <b className="type-strong text-content">{inFlightCount}</b> order{inFlightCount === 1 ? '' : 's'} still on the way
              {inFlightValue > 0 && <> · worth <b className="type-strong text-content">{inr(inFlightValue)}</b></>}
              {(stageCounts.delivered ?? 0) > 0 && <> · <b className="type-strong text-content">{stageCounts.delivered}</b> delivered in this period</>}
            </>
          ) : (stageCounts.delivered ?? 0) > 0
            ? <>Everything you ordered in this period has been delivered — <b className="type-strong text-content">{stageCounts.delivered}</b> order{stageCounts.delivered === 1 ? '' : 's'}.</>
            : 'No orders in this period yet.'}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-stretch">
          {STAGES.map((st, i) => {
            const n = stageCounts[st.key] ?? 0;
            const href = `/orders?status=${st.statuses.join(',')}&from=${range.from}&to=${range.to}&includeClosed=true`;
            const Tag = n > 0 ? 'button' : 'div';
            return (
              <Fragment key={st.key}>
                <Tag
                  {...(n > 0 ? { type: 'button' as const, onClick: () => navigate(href) } : {})}
                  className={cn(
                    'flex min-w-[150px] flex-1 items-center gap-3 rounded-card border p-3 text-left sm:flex-col sm:items-start sm:gap-2',
                    'bg-linear-to-b to-70% to-transparent',
                    n > 0
                      ? cn('bg-surface shadow-e1', st.tint, 'cursor-pointer transition-shadow duration-150 hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40')
                      : 'border-line/70 bg-surface',
                  )}
                >
                  <span className={cn('grid size-9 shrink-0 place-items-center rounded-control', n > 0 ? st.chip : 'bg-surface-3 text-subtle')}>
                    {st.icon}
                  </span>
                  <div className="flex min-w-0 flex-1 items-center justify-between gap-2 sm:w-full">
                    <span className={cn('min-w-0 truncate type-label', n > 0 ? 'text-content' : 'text-subtle')}>{st.label}</span>
                    <span className={cn('type-metric-sm', n > 0 ? 'text-content' : 'text-subtle')}>{n}</span>
                  </div>
                  {/* The rail under a stage is what makes the row read as a track rather than five
                      unrelated boxes — full-strength where there is something, a hairline where there
                      is not, so the shape of the pipeline is legible before any number is read. */}
                  <span className={cn('hidden h-1 w-full rounded-pill sm:block', n > 0 ? st.rail : 'bg-line')} />
                </Tag>
                {i < STAGES.length - 1 && (
                  <ChevronRight aria-hidden className="hidden shrink-0 self-center text-subtle lg:block" />
                )}
              </Fragment>
            );
          })}
        </div>
      </Card>

      {/* Recent orders + recent invoices */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card
          title={<span className="flex items-center gap-2"><ClipboardList className="size-4 text-brand-600" /> Recent orders</span>}
          extra={<button type="button" onClick={() => navigate(ordersHref)} className="type-label-strong text-brand-600 hover:text-brand-700">View all</button>}
          density="compact"
        >
          {ordLoading ? <ListSkeleton /> : recentOrders.length ? (
            <div className="space-y-1">
              {recentOrders.map((o: any) => {
                const first = o.products?.[0];
                const more = Math.max(0, (o.products?.length ?? 0) - 1);
                return (
                  <button
                    key={o.id} type="button" onClick={() => navigate(`/orders/${o.id}`)}
                    className="flex w-full items-center gap-3 rounded-control border border-transparent px-2 py-2 text-left transition hover:border-line hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                  >
                    {/* The identity mark is the PRODUCT, not a repeated cart glyph — a mark that is the
                        same on every row is noise, and this one varies with what was actually ordered
                        (and stays the same colour for that product everywhere in the app). */}
                    <EntityAvatar name={first?.name ?? o.orderNo} size={38} square />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="truncate type-body-medium text-content">{o.orderNo}</span>
                        <Pill color={STATUS_PILL[o.status] ?? 'default'}>{orderStatusLabel(o.status)}</Pill>
                      </div>
                      <div className="mt-0.5 truncate type-label text-subtle">
                        {o.orderDate ? formatIstDate(o.orderDate) : '—'}
                        {first && <> · {first.name}{first.variantLabel ? ` ${first.variantLabel}` : ''}{first.qty ? ` ×${first.qty}` : ''}{more > 0 ? ` +${more} more` : ''}</>}
                      </div>
                    </div>
                    <div className="shrink-0 text-right type-body-strong tabular-nums text-content">{inr2(o.grandTotal)}</div>
                    <ChevronRight className="size-4 shrink-0 text-subtle" />
                  </button>
                );
              })}
            </div>
          ) : (
            <EmptyPanel
              icon={<ClipboardList className="size-7" />}
              title="No orders in the last 6 months"
              detail="Anything you order will show up here with its live status."
              action={<Button variant="ghost" size="sm" icon={<Plus className="size-4" />} onClick={() => navigate('/orders/new')}>Place an order</Button>}
            />
          )}
        </Card>

        <Card
          title={<span className="flex items-center gap-2"><FileText className="size-4 text-brand-600" /> Recent invoices</span>}
          extra={<button type="button" onClick={() => navigate('/billing/invoices')} className="type-label-strong text-brand-600 hover:text-brand-700">View all</button>}
          density="compact"
        >
          {invLoading ? <ListSkeleton /> : recentInvoices.length ? (
            <div className="space-y-1">
              {recentInvoices.map((i: any) => (
                <button
                  key={i.id} type="button" onClick={() => navigate(`/billing/invoices/${i.id}`)}
                  className="flex w-full items-center gap-3 rounded-control border border-transparent px-2 py-2 text-left transition hover:border-line hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                >
                  {/* No mark on an invoice row. An EntityAvatar here could only carry a CONSTANT receipt
                      glyph — an invoice has no varying subject to mark the way an order has its
                      product — and one identical picture per row is noise, not identity. */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="truncate type-body-medium text-content">{i.invoiceNo}</span>
                      {i.status && <Pill color={INV_PILL[i.status] ?? 'default'}>{statusLabel(i.status)}</Pill>}
                    </div>
                    <div className="mt-0.5 truncate type-label text-subtle">
                      {i.invoiceDate ? formatIstDate(i.invoiceDate) : '—'}{i.dueDate ? ` · due ${formatIstDate(i.dueDate)}` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-right type-body-strong tabular-nums text-content">{inr2(i.grandTotal)}</div>
                  <ChevronRight className="size-4 shrink-0 text-subtle" />
                </button>
              ))}
            </div>
          ) : (
            <EmptyPanel
              icon={<FileText className="size-7" />}
              title="No invoices yet"
              detail="An invoice appears here once an order of yours is billed — that is also when its payment due date starts."
            />
          )}
        </Card>
      </div>

      {/* Sales rep + quick links */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title={<span className="flex items-center gap-2"><Phone className="size-4 text-brand-600" /> Your sales rep</span>}>
          {rep?.rep ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {/* A PERSON gets the circle; the hue is derived from their name, so the same rep is
                    the same colour on every page that draws them. */}
                <EntityAvatar name={rep.rep.name} src={rep.rep.photoUrl} size={48} />
                <div className="min-w-0 flex-1">
                  <div className="truncate type-body-strong text-content">{rep.rep.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <RoleBadge role={rep.rep.role} />
                    {rep.coverage?.name && (
                      <span className="inline-flex items-center gap-1 rounded-pill border border-line bg-surface px-2 py-px type-label text-muted">
                        <MapPin className="size-3" /> {rep.coverage.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {rep.rep.mobile && (
                <div className="grid grid-cols-2 gap-2">
                  <a href={`tel:${rep.rep.mobile}`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-control border border-brand-500/30 bg-brand-500/10 px-3 py-2 type-body-medium text-brand-700 transition hover:bg-brand-500/20 dark:text-brand-300">
                    <Phone className="size-4" /> Call
                  </a>
                  <a href={`https://wa.me/${String(rep.rep.mobile).replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 rounded-control border border-line bg-surface px-3 py-2 type-body-medium text-content transition hover:border-brand-300 hover:text-brand-700">
                    <MessageCircle className="size-4" /> WhatsApp
                  </a>
                </div>
              )}
              {rep.company?.name && (
                <div className="border-t border-line pt-2.5 type-label text-subtle">
                  {rep.company.name}
                  {rep.company.phone && <> · <a href={`tel:${rep.company.phone}`} className="type-label-strong text-brand-600 hover:text-brand-700">{rep.company.phone}</a></>}
                </div>
              )}
            </div>
          ) : rep?.company?.name ? (
            <div className="type-body text-muted">
              No rep assigned yet — contact <b className="type-strong text-content">{rep.company.name}</b>
              {rep.company.phone && <> · <a href={`tel:${rep.company.phone}`} className="type-strong text-brand-600">{rep.company.phone}</a></>}
            </div>
          ) : (
            <EmptyPanel icon={<Phone className="size-7" />} title="No sales rep assigned yet" detail="Once a rep covers your territory, their number appears here." />
          )}
        </Card>

        <Card className="xl:col-span-2" title="Quick links">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            <QuickLink tone="brand" icon={<Wallet />} label="Full statement" hint="Every entry" onClick={() => navigate('/account')} />
            <QuickLink tone="gold" icon={<FileText />} label="Invoices" hint="Bills & dues" badge={badges.openInvoices} onClick={() => navigate('/billing/invoices')} />
            <QuickLink tone="sky" icon={<Undo2 />} label="Returns" hint="Send stock back" badge={badges.returnsActionable} onClick={() => navigate('/returns')} />
            <QuickLink tone="danger" icon={<MessageSquareWarning />} label="Complaints" hint="Raise an issue" badge={badges.openComplaints} onClick={() => navigate('/complaints')} />
            <QuickLink tone="violet" icon={<ShoppingCart />} label="My orders" hint="Track everything" badge={badges.ordersActive} onClick={() => navigate('/orders')} />
            <QuickLink tone="brand" icon={<Plus />} label="New order" hint={trendCount > 0 ? 'Buy again' : 'Start an order'} onClick={() => navigate('/orders/new')} />
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── Local building blocks ──────────────────────────────────────────────

/** A two-field pill for the letterhead: what it is at label weight, the value at full contrast. */
function DetailChip({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-pill border border-line bg-surface px-2.5 py-1 shadow-e1">
      <span className="shrink-0 type-label text-subtle">{label}</span>
      <span className="truncate type-label-strong text-content">{children}</span>
    </span>
  );
}

/**
 * Per-tone styling for the quick-link tiles. Six identical grey boxes are a menu you have to read
 * word by word; six tinted ones are navigable at a glance, and the hue is the destination's own
 * (dues green, bills gold, complaints red…) rather than decoration.
 */
const QL_TONE: Record<string, { chip: string; tile: string }> = {
  brand: { chip: 'bg-brand-500/15 text-brand-700 dark:text-brand-300', tile: 'border-brand-500/30 from-brand-500/[0.08] hover:border-brand-500/50' },
  gold: { chip: 'bg-harvest-400/20 text-harvest-700 dark:text-harvest-300', tile: 'border-harvest-400/40 from-harvest-400/[0.10] hover:border-harvest-400/60' },
  sky: { chip: 'bg-sky-500/15 text-sky-600 dark:text-sky-400', tile: 'border-sky-500/30 from-sky-500/[0.08] hover:border-sky-500/50' },
  danger: { chip: 'bg-danger-500/12 text-danger-600 dark:text-danger-500', tile: 'border-danger-500/30 from-danger-500/[0.08] hover:border-danger-500/50' },
  violet: { chip: 'bg-violet-500/15 text-violet-600 dark:text-violet-400', tile: 'border-violet-500/30 from-violet-500/[0.08] hover:border-violet-500/50' },
};

function QuickLink({ icon, label, hint, badge, tone = 'brand', onClick }: {
  icon: ReactNode; label: string; hint?: string; badge?: number; tone?: keyof typeof QL_TONE | string; onClick: () => void;
}) {
  const t = QL_TONE[tone] ?? QL_TONE.brand;
  return (
    <button
      type="button" onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-card border bg-surface p-3 text-left shadow-e1 transition-shadow duration-150',
        'bg-linear-to-b to-70% to-transparent hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        t.tile,
      )}
    >
      <span className={cn('grid size-10 shrink-0 place-items-center rounded-control [&>svg]:size-5', t.chip)}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate type-body-medium text-content">{label}</span>
        {hint && <span className="block truncate type-label text-subtle">{hint}</span>}
      </span>
      {badge != null && badge > 0 && (
        <span className="shrink-0 rounded-pill bg-brand-600 px-1.5 py-px type-caption-strong text-white">{badge}</span>
      )}
    </button>
  );
}

/** Themed tooltip — order value + count for the hovered month. Semantic classes → flips light/dark. */
function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload ?? {};
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-e3">
      <div className="mb-0.5 font-semibold text-content">{String(label)}</div>
      <div className="text-muted">Order value: <span className="font-semibold text-content">{inr2(p.value)}</span></div>
      <div className="text-muted">{p.count ?? 0} order{p.count === 1 ? '' : 's'}</div>
    </div>
  );
}

function ListSkeleton() {
  return <div className="space-y-2 p-1">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
}

/**
 * An empty panel that says what WOULD be here and, where there is one, offers the action that fills
 * it. "No invoices yet." on its own tells a dealer nothing about why, or what to do next.
 */
function EmptyPanel({ icon, title, detail, action }: { icon: ReactNode; title: string; detail?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      <span className="grid size-12 place-items-center rounded-pill bg-brand-500/10 text-brand-600 dark:text-brand-400">{icon}</span>
      <span className="type-body-strong text-content">{title}</span>
      {detail && <span className="max-w-sm type-label text-subtle">{detail}</span>}
      {action && <span className="mt-1">{action}</span>}
    </div>
  );
}
